import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { StatCard, Loading, EmptyState, StatusBadge } from "@/components/common";
import PeriodFilter, { usePeriod } from "@/components/PeriodFilter";
import ProductionBreakdown from "@/pages/ProductionBreakdown";
import { numberID, formatDate } from "@/lib/format";
import { wibToday } from "@/lib/bizdate";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import {
  Factory, Target, CheckCircle2, Percent, Scissors, Shirt, Sparkles, ClipboardCheck,
  ThumbsUp, XCircle, RefreshCw, PackageCheck, AlertTriangle, Clock, Boxes, ShieldCheck,
} from "lucide-react";

export default function ProductionDashboard() {
  const { period, start_date, end_date, setPeriod } = usePeriod();
  const [data, setData] = useState(null);
  const [mat, setMat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [tgtForm, setTgtForm] = useState({ date: wibToday(), target_qty: "" });

  const params = { period };
  if (period === "custom") { params.start_date = start_date; params.end_date = end_date; }
  const key = JSON.stringify(params) + ":" + tick;

  useEffect(() => {
    if (period === "custom" && (!start_date || !end_date)) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      api.get("/dashboard/production", { params }).then((r) => setData(r.data)).catch(() => {}),
      api.get("/dashboard/production-material").then((r) => setMat(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const saveTarget = async () => {
    if (!tgtForm.target_qty || Number(tgtForm.target_qty) <= 0) return;
    await api.post("/production/daily-targets", { date: tgtForm.date, target_qty: Number(tgtForm.target_qty) }).catch(() => {});
    setTgtForm({ ...tgtForm, target_qty: "" });
    setTick((t) => t + 1);
  };

  const cs = data?.current_state;
  const pp = data?.period_performance;
  const kpi = data?.period_kpi;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="prod-dash-title">Dashboard Produksi</h1>
          <p className="text-sm text-slate-500 mt-1">Ringkasan produksi konveksi berdasarkan periode terpilih.</p>
        </div>
        <PeriodFilter period={period} start_date={start_date} end_date={end_date} onChange={setPeriod} />
      </div>

      {loading ? <Loading label="Memuat dashboard produksi..." /> : !cs ? <EmptyState title="Data tidak tersedia" /> : (
        <>
          {/* B. KINERJA PERIODE — berdasarkan tanggal kejadian (WIB) */}
          <div className="mb-3 flex items-center gap-2">
            <Percent className="h-4 w-4 text-sky-600" />
            <h3 className="font-display text-lg font-semibold text-slate-900">Kinerja Periode</h3>
            <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-600">Berdasarkan tanggal kejadian (WIB)</span>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            <StatCard label="Output Produksi (Finishing)" value={numberID(pp.output_finishing)} icon={Sparkles} accent="emerald" testId="pp-output" />
            <StatCard label="Target Produksi" value={pp.target_set ? numberID(pp.target) : "Belum ditetapkan"} icon={Target} accent="slate" testId="pp-target" />
            <StatCard label="Achievement" value={pp.achievement === null ? "Tidak tersedia" : `${pp.achievement}%`} icon={Percent} accent="sky" hint="Output Finishing / Target periode" testId="pp-achievement" />
            <StatCard label="QC Passed" value={numberID(pp.qc_passed)} icon={ThumbsUp} accent="emerald" testId="pp-passed" />
            <StatCard label="QC Reject" value={numberID(pp.qc_reject)} icon={XCircle} accent="rose" testId="pp-reject" />
            <StatCard label="Rework" value={numberID(pp.rework)} icon={RefreshCw} accent="amber" testId="pp-rework" />
            <StatCard label="Scrap" value={numberID(pp.scrap)} icon={XCircle} accent="rose" testId="pp-scrap" />
            <StatCard label="Finished Goods" value={numberID(pp.finished_goods)} icon={PackageCheck} accent="emerald" hint="QC Passed masuk barang jadi" testId="pp-fg" />
            <StatCard label="Transfer ke Pusat" value={numberID(pp.transfer_to_pusat)} icon={Boxes} accent="indigo" testId="pp-transfer" />
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <div><label className="block text-xs text-slate-500 mb-1">Tanggal Target</label><input type="date" value={tgtForm.date} onChange={(e) => setTgtForm({ ...tgtForm, date: e.target.value })} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" data-testid="pp-target-date" /></div>
            <div><label className="block text-xs text-slate-500 mb-1">Target Qty</label><input type="number" value={tgtForm.target_qty} onChange={(e) => setTgtForm({ ...tgtForm, target_qty: e.target.value })} className="w-32 rounded-lg border border-slate-300 px-2 py-1 text-sm" data-testid="pp-target-qty" /></div>
            <button onClick={saveTarget} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700" data-testid="pp-target-save">Simpan Target Harian</button>
          </div>

          {kpi && (
            <>
              {/* BLOK 1 — Ringkasan KPI Produksi (periode) */}
              <div className="mt-6 flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4 text-sky-600" />
                <h3 className="font-display text-lg font-semibold text-slate-900">Ringkasan KPI Produksi</h3>
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-600">Mengikuti filter periode (WIB)</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                <StatCard label="Roll Unik Terpakai" value={numberID(kpi.unique_rolls)} icon={Boxes} accent="indigo" testId="kpi-rolls" />
                <StatCard label="Kg Kain Terpakai" value={`${numberID(kpi.kg_used)} kg`} icon={Boxes} accent="slate" testId="kpi-kg" />
                <StatCard label="Waste Kain" value={`${numberID(kpi.waste_kg)} kg`} icon={AlertTriangle} accent="amber" testId="kpi-waste" />
                <StatCard label="Cutting (pcs)" value={numberID(kpi.cutting)} icon={Scissors} accent="sky" testId="kpi-cutting" />
                <StatCard label="Sewing (pcs)" value={numberID(kpi.sewing)} icon={Shirt} accent="sky" testId="kpi-sewing" />
                <StatCard label="Finishing (pcs)" value={numberID(kpi.finishing)} icon={Sparkles} accent="emerald" testId="kpi-finishing" />
                <StatCard label="QC Masuk" value={numberID(kpi.qc_masuk)} icon={ClipboardCheck} accent="slate" hint="Masuk antrian QC (Finishing + Rework kembali)" testId="kpi-qcmasuk" />
                <StatCard label="QC Diperiksa" value={numberID(kpi.qc_checked)} icon={ClipboardCheck} accent="sky" hint="Passed + Reject" testId="kpi-qcchecked" />
                <StatCard label="QC Passed (pcs)" value={numberID(kpi.qc_passed)} icon={ThumbsUp} accent="emerald" testId="kpi-passed" />
                <StatCard label="QC Reject (pcs)" value={numberID(kpi.qc_reject)} icon={XCircle} accent="rose" testId="kpi-reject" />
                <StatCard label="Rework (pcs)" value={numberID(kpi.rework)} icon={RefreshCw} accent="amber" testId="kpi-rework" />
                <StatCard label="Scrap (pcs)" value={numberID(kpi.scrap)} icon={XCircle} accent="rose" testId="kpi-scrap" />
              </div>
              {(!kpi.catch_weight_enabled || kpi.rolls_pending_recon > 0 || kpi.legacy_issue_qty > 0) && (
                <p className="mt-2 text-[11px] text-amber-600" data-testid="kpi-limitation">
                  Keterbatasan data: {kpi.catch_weight_enabled ? "" : "fitur Catch Weight non-aktif — identitas roll/berat legacy tidak tersedia. "}
                  {kpi.rolls_pending_recon > 0 ? `${kpi.rolls_pending_recon} roll belum direkonsiliasi (kg belum final). ` : ""}
                  {kpi.legacy_issue_qty > 0 ? `${numberID(kpi.legacy_issue_qty)} qty pengeluaran bahan legacy (tanpa identitas roll, tidak dikonversi ke kg).` : ""}
                </p>
              )}
              {data.corrections && Object.values(data.corrections).some((v) => v !== 0) && (
                <p className="mt-1 text-[11px] text-slate-500" data-testid="kpi-corrections">
                  Termasuk kontribusi Koreksi (Owner) pada periode: {Object.entries(data.corrections).filter(([, v]) => v !== 0).map(([f, v]) => `${f} ${v > 0 ? "+" : ""}${v}`).join(", ")}
                </p>
              )}

              {/* BLOK 2 — Grafik Tren Produksi */}
              <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="prod-trend-chart">
                <h3 className="mb-3 font-display text-base font-semibold text-slate-900">Tren Produksi</h3>
                {data.trend && data.trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={data.trend} margin={{ top: 4, right: 12, left: -8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="cutting" name="Cutting" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="sewing" name="Sewing" stroke="#6366f1" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="finishing" name="Finishing" stroke="#10b981" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="qc_passed" name="QC Passed" stroke="#22c55e" strokeWidth={2} strokeDasharray="4 2" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <EmptyState title="Belum ada data tren pada periode ini" />}
              </div>

              {/* BLOK 2b — Tren Kualitas Produksi (QC Pass Rate) */}
              {(() => {
                const q = data.quality;
                const qt = (data.quality_trend || []).filter((p) => p.qc_checked > 0);
                const rateLabel = !q || q.pass_rate === null ? "N/A" : `${q.pass_rate}%`;
                return (
                  <div className="mt-6" data-testid="qc-quality-section">
                    <div className="mb-3 flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-emerald-600" />
                      <h3 className="font-display text-lg font-semibold text-slate-900">Tren Kualitas Produksi</h3>
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">Mengikuti filter periode (WIB)</span>
                      {q && q.adjusted && (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-600" data-testid="qc-adjusted-badge">Disesuaikan (Adjusted)</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                      <StatCard label={q && q.adjusted ? "QC Pass Rate (Adjusted)" : "QC Pass Rate Periode"} value={rateLabel} icon={Percent} accent="emerald" hint="QC Passed / (Passed + Reject) seluruh periode" testId="qc-rate-period" />
                      <StatCard label="QC Diperiksa" value={numberID(q ? q.qc_checked : 0)} icon={ClipboardCheck} accent="sky" hint="Passed + Reject" testId="qc-checked-period" />
                      <StatCard label="QC Passed" value={numberID(q ? q.qc_passed : 0)} icon={ThumbsUp} accent="emerald" testId="qc-passed-period" />
                      <StatCard label="QC Reject" value={numberID(q ? q.qc_reject : 0)} icon={XCircle} accent="rose" testId="qc-reject-period" />
                    </div>
                    {q && q.adjusted && (
                      <p className="mt-2 text-[11px] text-amber-600" data-testid="qc-adjusted-note">
                        Angka memuat Koreksi (Owner) pada field QC yang tidak berasal dari kejadian pemeriksaan, sehingga disebut QC Pass Rate <b>disesuaikan (Adjusted)</b> — bukan tingkat kelulusan pemeriksaan murni.
                      </p>
                    )}

                    {/* QC Pass Rate harian */}
                    <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="qc-rate-chart">
                      <h4 className="mb-3 text-sm font-semibold text-slate-700">QC Pass Rate Harian (%)</h4>
                      {qt.length > 0 ? (
                        <ResponsiveContainer width="100%" height={240}>
                          <LineChart data={qt} margin={{ top: 4, right: 12, left: -8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                            <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v) => (v === null || v === undefined ? "N/A" : `${v}%`)} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Line type="monotone" dataKey="pass_rate" name="QC Pass Rate" stroke="#10b981" strokeWidth={2} connectNulls={false} dot={{ r: 2 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : <EmptyState title="Tidak ada pemeriksaan QC pada periode ini (N/A)" />}
                    </div>

                    {/* Finishing vs QC Passed harian — batang berdampingan */}
                    <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="qc-finishing-chart">
                      <h4 className="mb-1 text-sm font-semibold text-slate-700">Finishing vs QC Passed (Harian)</h4>
                      <p className="mb-3 text-[11px] text-slate-400">Catatan: selisih harian <b>bukan</b> rasio efisiensi langsung — waktu finishing dan pemeriksaan QC dapat berbeda hari.</p>
                      {(data.quality_trend || []).length > 0 ? (
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={data.quality_trend} margin={{ top: 4, right: 12, left: -8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="finishing" name="Finishing" fill="#6366f1" radius={[3, 3, 0, 0]} />
                            <Bar dataKey="qc_passed" name="QC Passed" fill="#10b981" radius={[3, 3, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : <EmptyState title="Belum ada data pada periode ini" />}
                    </div>
                  </div>
                );
              })()}

              {/* BLOK 3 — Ringkasan Pemakaian Bahan */}
              <div className="mt-6 flex items-center gap-2">
                <Boxes className="h-4 w-4 text-indigo-600" />
                <h3 className="font-display text-lg font-semibold text-slate-900">Pemakaian Bahan (Periode)</h3>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <StatCard label="Roll Unik Terpakai" value={numberID(kpi.unique_rolls)} accent="indigo" testId="mu-rolls" />
                <StatCard label="Total Kg Terpakai" value={`${numberID(kpi.kg_used)} kg`} accent="slate" testId="mu-kg" />
                <StatCard label="Total Waste" value={`${numberID(kpi.waste_kg)} kg`} accent="amber" testId="mu-waste" />
                <StatCard label="Roll Belum Rekonsiliasi" value={numberID(kpi.rolls_pending_recon)} accent="slate" hint="kg belum final" testId="mu-pending" />
              </div>

              {/* BLOK 4 — Tabel Ringkasan Harian */}
              <div className="mt-6">
                <h3 className="mb-3 font-display text-lg font-semibold text-slate-900">Rekap Harian</h3>
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Tanggal</TableHead><TableHead className="text-right">Roll</TableHead>
                      <TableHead className="text-right">Kg</TableHead><TableHead className="text-right">Waste</TableHead>
                      <TableHead className="text-right">Cutting</TableHead><TableHead className="text-right">Sewing</TableHead>
                      <TableHead className="text-right">Finishing</TableHead><TableHead className="text-right">QC Masuk</TableHead>
                      <TableHead className="text-right">Diperiksa</TableHead><TableHead className="text-right">Passed</TableHead>
                      <TableHead className="text-right">Reject</TableHead><TableHead className="text-right">Rework</TableHead>
                      <TableHead className="text-right">Scrap</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {(data.daily || []).length === 0 ? (
                        <TableRow><TableCell colSpan={13} className="text-center text-sm text-slate-400 py-6">Tidak ada aktivitas pada periode ini</TableCell></TableRow>
                      ) : data.daily.map((r) => (
                        <TableRow key={r.date} data-testid={`daily-${r.date}`}>
                          <TableCell className="font-mono text-xs">{r.date}</TableCell>
                          <TableCell className="text-right">{numberID(r.rolls)}</TableCell>
                          <TableCell className="text-right">{numberID(r.kg_used)}</TableCell>
                          <TableCell className="text-right">{numberID(r.waste_kg)}</TableCell>
                          <TableCell className="text-right">{numberID(r.cutting)}</TableCell>
                          <TableCell className="text-right">{numberID(r.sewing)}</TableCell>
                          <TableCell className="text-right">{numberID(r.finishing)}</TableCell>
                          <TableCell className="text-right">{numberID(r.qc_masuk)}</TableCell>
                          <TableCell className="text-right">{numberID(r.qc_checked)}</TableCell>
                          <TableCell className="text-right text-emerald-600">{numberID(r.qc_passed)}</TableCell>
                          <TableCell className="text-right text-rose-600">{numberID(r.qc_reject)}</TableCell>
                          <TableCell className="text-right text-amber-600">{numberID(r.rework)}</TableCell>
                          <TableCell className="text-right text-rose-600">{numberID(r.scrap)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          )}

          {/* A. KONDISI SAAT INI — backlog sekarang (tidak difilter periode) */}
          <div className="mt-6 flex items-center gap-2">
            <Boxes className="h-4 w-4 text-indigo-600" />
            <h3 className="font-display text-lg font-semibold text-slate-900">Kondisi Saat Ini</h3>
            <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-600">Backlog sekarang · tidak mengikuti filter periode</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            <StatCard label="Production Order Aktif" value={numberID(cs.po_active)} icon={Factory} accent="indigo" testId="pk-active" />
            <StatCard label="Cutting Pending" value={numberID(cs.cutting_pending)} icon={Scissors} accent="amber" testId="pk-cutting" />
            <StatCard label="Sewing Pending" value={numberID(cs.sewing_pending)} icon={Shirt} accent="amber" testId="pk-sewing" />
            <StatCard label="Finishing Pending" value={numberID(cs.finishing_pending)} icon={Sparkles} accent="amber" testId="pk-finishing" />
            <StatCard label="QC Pending" value={numberID(cs.qc_pending)} icon={ClipboardCheck} accent="amber" testId="pk-qc" />
            <StatCard label="Rework Pending" value={numberID(cs.rework_pending)} icon={RefreshCw} accent="amber" testId="pk-rework" />
            <StatCard label="Produksi Terlambat" value={numberID(cs.overdue)} icon={Clock} accent="rose" testId="pk-overdue" />
            <StatCard label="Barang Jadi Menunggu Transfer" value={numberID(cs.fg_waiting_transfer)} icon={PackageCheck} accent="indigo" hint="Di Inventory Konveksi" testId="pk-fg" />
            <StatCard label="Potensi Kekurangan Bahan" value={numberID(cs.material_shortage_potensi)} icon={AlertTriangle} accent="amber" testId="pk-shortage-potensi" />
            <StatCard label="Kekurangan Bahan" value={numberID(cs.material_shortage_actual)} icon={AlertTriangle} accent="rose" testId="pk-shortage-actual" />
          </div>

          {/* Material dashboard */}
          {mat && (
            <div className="mt-8">
              <h3 className="mb-1 font-display text-lg font-semibold text-slate-900">Kebutuhan Bahan (Material Request)</h3>
              <p className="mb-3 text-xs text-slate-400">Snapshot berdasarkan MR & stok Konveksi saat ini — tidak dijumlah antar tanggal.</p>
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                <StatCard label="Rencana Roll Kain" value={numberID(mat.rencana)} accent="slate" hint="Total MR aktif" testId="mat-rencana" />
                <StatCard label="Sudah Dikeluarkan" value={numberID(mat.dikeluarkan)} accent="emerald" testId="mat-keluar" />
                <StatCard label="Sisa Kebutuhan" value={numberID(mat.sisa)} accent="amber" hint="MR draft belum keluar" testId="mat-sisa" />
                <StatCard label="Potensi Kekurangan" value={numberID(mat.potensi_count)} accent="amber" hint="MR draft" testId="mat-potensi" />
                <StatCard label="Kekurangan Bahan" value={numberID(mat.actual_count)} accent="rose" hint="MR terkonfirmasi" testId="mat-actual" />
              </div>
              {(mat.potensi.length > 0 || mat.actual.length > 0) && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Jenis</TableHead><TableHead>MR</TableHead><TableHead>Bahan</TableHead><TableHead className="text-right">Diminta</TableHead><TableHead className="text-right">Tersedia</TableHead><TableHead className="text-right">Kurang</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {mat.actual.map((s, i) => (
                        <TableRow key={`a${i}`} data-testid={`mat-actual-${i}`}>
                          <TableCell><span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-600">KEKURANGAN</span></TableCell>
                          <TableCell className="font-mono text-xs">{s.mr_no}</TableCell>
                          <TableCell>{s.item_name}</TableCell>
                          <TableCell className="text-right">{numberID(s.diminta)} {s.unit}</TableCell>
                          <TableCell className="text-right">{numberID(s.tersedia)}</TableCell>
                          <TableCell className="text-right font-semibold text-rose-600">{numberID(s.kurang)}</TableCell>
                        </TableRow>
                      ))}
                      {mat.potensi.map((s, i) => (
                        <TableRow key={`p${i}`} data-testid={`mat-potensi-${i}`}>
                          <TableCell><span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-600">POTENSI</span></TableCell>
                          <TableCell className="font-mono text-xs">{s.mr_no}</TableCell>
                          <TableCell>{s.item_name}</TableCell>
                          <TableCell className="text-right">{numberID(s.diminta)} {s.unit}</TableCell>
                          <TableCell className="text-right">{numberID(s.tersedia)}</TableCell>
                          <TableCell className="text-right font-semibold text-amber-600">{numberID(s.kurang)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {/* Progress by PO */}
          <ProductionBreakdown period={period} start_date={start_date} end_date={end_date} />

          <div className="mt-8">
            <h3 className="mb-1 font-display text-lg font-semibold text-slate-900">Progress per Production Order</h3>
            <p className="mb-3 text-xs text-slate-400">Progress kumulatif PO (snapshot) — bukan Achievement periode.</p>
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
              {data.orders.length === 0 ? <EmptyState title="Belum ada Production Order" icon={Factory} /> : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>No PO</TableHead><TableHead>Bahan</TableHead><TableHead>Warna</TableHead><TableHead className="text-right">Target</TableHead><TableHead className="text-right">Actual</TableHead><TableHead>Progress</TableHead><TableHead>Tahap</TableHead><TableHead>Deadline</TableHead><TableHead>Prioritas</TableHead><TableHead>Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.orders.map((o) => (
                      <TableRow key={o.id} data-testid={`prod-dash-row-${o.po_prod_no}`}>
                        <TableCell className="font-mono text-xs">{o.po_prod_no}</TableCell>
                        <TableCell className="text-sm">{o.fabric_name}</TableCell>
                        <TableCell><span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full border border-slate-300" style={{ background: o.color_hex }} />{o.color_name}</span></TableCell>
                        <TableCell className="text-right">{numberID(o.target)}</TableCell>
                        <TableCell className="text-right text-emerald-600 font-medium">{numberID(o.actual)}</TableCell>
                        <TableCell><div className="flex items-center gap-2"><div className="h-1.5 w-20 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-indigo-500" style={{ width: `${o.progress}%` }} /></div><span className="text-xs text-slate-500">{o.progress}%</span></div></TableCell>
                        <TableCell><span className="text-xs font-medium text-slate-600">{o.stage}</span></TableCell>
                        <TableCell className="text-xs">{o.deadline ? <span className={o.overdue ? "font-semibold text-rose-600" : "text-slate-500"}>{formatDate(o.deadline)}</span> : <span className="text-slate-300">-</span>}</TableCell>
                        <TableCell className="text-xs text-slate-500">{o.priority}</TableCell>
                        <TableCell><StatusBadge status={o.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
